import PropTypes from 'prop-types';
import { useNodeById, useSensitivityPlot } from '@/stores/analysisStore';
import { analysisActions } from '@/stores/analysisStore';
import TornadoChart from '@/components/charts/TornadoChart';
import SensitivityForm from './SensitivityForm';
import { getSensitivityXaxisLabel } from '@/utils/analysisUtils';
import { useAnalysisProgress } from '@/stores/analysisStatusStore';
import { isDefined } from '@/utils/miscUtils';
import ProgressBar from '@/components/core/ProgressBar';
import { usePlotSync, usePlotSyncHandler } from '@/components/providers/PlotSyncProvider';
import { useEffect, useCallback, useMemo, useRef } from 'react';

const SensitivityPlot = ({ type }) => {
  const plot = useSensitivityPlot(type);
  const { nodeId, results } = plot;
  const node = useNodeById(nodeId);
  const { setParams } = usePlotSync();
  const updatePlot = useCallback(
    async formData => {
      setParams(formData);
      await analysisActions.updatePlot(`sensitivity_${type}`, formData);
    },
    [type, setParams],
  );

  const xAxisLabel = node ? getSensitivityXaxisLabel(plot, node, type) : '';
  const progress = useAnalysisProgress('sensitivity');

  const defaultValues = useMemo(() => {
    return {
      nodeId: plot.nodeId,
      levelizeBy: plot.levelizeBy,
      levelizeUnit: plot.levelizeUnit,
      levelizableIos: plot.levelizable_ios,
    };
  }, [plot.nodeId, plot.levelizeBy, plot.levelizeUnit, plot.levelizable_ios]);

  const { syncData } = usePlotSyncHandler(updatePlot, defaultValues);
  const hasSynced = useRef(false);

  useEffect(() => {
    if (!hasSynced.current) {
      syncData();
      hasSynced.current = true;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isDefined(progress) && progress < 1) {
    return <ProgressBar value={progress * 100} />;
  }

  return (
    <div className="relative">
      {node ? (
        <SensitivityForm key={plot.nodeId} onSubmit={updatePlot} defaultValues={defaultValues} />
      ) : (
        <span className="loading loading-dots loading-md"></span>
      )}
      {results && (
        <TornadoChart
          xAxisLabel={xAxisLabel}
          datasets={results.datasets}
          labels={results.labels}
          baseline={results.baseline}
          type={type}
        />
      )}
    </div>
  );
};

SensitivityPlot.propTypes = {
  type: PropTypes.string,
};

export default SensitivityPlot;
